import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isAxiosError } from "axios"
import {
    AdminAuditEvent,
    AdminActionTypeDTO,
    CommunityAdminDTO,
    exportAdminAuditLog,
    getAdminAuditActionTypes,
    IListAdminAuditLogResponse,
    JournalExportFormat,
    listAdminAuditLog,
    listCommunityAdmins,
} from "@/shared/api/admin-audit-log"
import { JournalExportButtons } from "@/features/journal-export"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Pagination } from "@/shared/ui/pagination"
import { getDefaultPeriod, PeriodPicker, PeriodValue } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { PageHeader } from "@/widgets/page-header"
import { resolveActionTypeLabel } from "../config/action-type-labels"
import { resolveResultLabel } from "../config/result-labels"

const PAGE_SIZE = 25

interface SelectOption {
    value: string
    label: string
}

interface ErrorResponse {
    error_id?: string
}

const formatDateTime = (value: string): string => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date)
}

const resolveRequestError = (error: unknown): string => {
    if (isAxiosError<ErrorResponse>(error)) {
        return getErrorMessage(error.response?.data?.error_id)
    }

    return getErrorMessage(undefined)
}

const JsonDetails = ({ label, value }: { label: string; value: Record<string, unknown> | null }) => (
    <div className="min-w-0">
        <h3 className="mb-2 text-sm font-bold">{label}</h3>
        {value === null ? (
            <div className="text-sm text-[#828282]">Данные отсутствуют</div>
        ) : (
            <pre className="max-h-72 overflow-auto rounded-xl bg-white p-3 text-xs whitespace-pre-wrap break-all">
                {JSON.stringify(value, null, 2)}
            </pre>
        )}
    </div>
)

const EventDetails = ({ event }: { event: AdminAuditEvent }) => (
    <div className="grid grid-cols-1 gap-4 py-3 lg:grid-cols-2">
        <JsonDetails label="До изменения" value={event.before} />
        <JsonDetails label="После изменения" value={event.after} />
        {event.params !== null && (
            <div className="lg:col-span-2">
                <JsonDetails label="Параметры" value={event.params} />
            </div>
        )}
    </div>
)

export const AdminActionsPage = () => {
    const logRequestId = useRef(0)
    const referencesRequestId = useRef(0)
    const [adminIds, setAdminIds] = useState<string[]>([])
    const [actionTypes, setActionTypes] = useState<string[]>([])
    const [period, setPeriod] = useState<PeriodValue>(() => getDefaultPeriod())
    const [page, setPage] = useState(1)
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null)
    const [auditData, setAuditData] = useState<IListAdminAuditLogResponse | null>(null)
    const [logLoading, setLogLoading] = useState(false)
    const [logError, setLogError] = useState<string | null>(null)
    const [admins, setAdmins] = useState<CommunityAdminDTO[]>([])
    const [availableActionTypes, setAvailableActionTypes] = useState<AdminActionTypeDTO[]>([])
    const [referencesLoading, setReferencesLoading] = useState(false)
    const [referencesError, setReferencesError] = useState<string | null>(null)

    const fetchReferences = useCallback(async () => {
        const requestId = ++referencesRequestId.current
        setReferencesLoading(true)
        setReferencesError(null)

        try {
            const [adminsResponse, actionTypesResponse] = await Promise.all([
                listCommunityAdmins(),
                getAdminAuditActionTypes(),
            ])
            if (referencesRequestId.current !== requestId) return
            setAdmins(adminsResponse.data)
            if (referencesRequestId.current !== requestId) return
            setAvailableActionTypes(actionTypesResponse.data)
        } catch (error) {
            if (referencesRequestId.current !== requestId) return
            setAdmins([])
            if (referencesRequestId.current !== requestId) return
            setAvailableActionTypes([])
            if (referencesRequestId.current !== requestId) return
            setReferencesError(resolveRequestError(error))
        } finally {
            if (referencesRequestId.current !== requestId) return
            setReferencesLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchReferences()
    }, [fetchReferences])

    const fetchAuditLog = useCallback(async () => {
        const requestId = ++logRequestId.current
        setLogLoading(true)
        setLogError(null)
        setExpandedEventId(null)

        try {
            const response = await listAdminAuditLog({
                adminIds,
                actionTypes,
                periodFrom: period.from,
                periodTo: period.to,
                page,
                pageSize: PAGE_SIZE,
            })
            if (logRequestId.current !== requestId) return
            setAuditData(response.data)
        } catch (error) {
            if (logRequestId.current !== requestId) return
            setAuditData(null)
            if (logRequestId.current !== requestId) return
            setLogError(resolveRequestError(error))
        } finally {
            if (logRequestId.current !== requestId) return
            setLogLoading(false)
        }
    }, [actionTypes, adminIds, page, period.from, period.to])

    useEffect(() => {
        fetchAuditLog()
    }, [fetchAuditLog])

    const adminOptions = useMemo<SelectOption[]>(
        () => admins.map((admin) => ({
            value: admin.id,
            label: `${admin.login}${admin.is_deleted ? " (удалён)" : ""}`,
        })),
        [admins]
    )
    const selectedAdminOptions = useMemo(
        () => adminOptions.filter((option) => adminIds.includes(option.value)),
        [adminIds, adminOptions]
    )
    const actionTypeOptions = useMemo<SelectOption[]>(
        () => availableActionTypes.map((actionType) => ({
            value: actionType.action_type,
            label: resolveActionTypeLabel(actionType.action_type),
        })),
        [availableActionTypes]
    )
    const selectedActionTypeOptions = useMemo(
        () => actionTypeOptions.filter((option) => actionTypes.includes(option.value)),
        [actionTypeOptions, actionTypes]
    )
    const deletedAdminIds = useMemo(
        () => new Set(admins.filter((admin) => admin.is_deleted).map((admin) => admin.id)),
        [admins]
    )

    // react-select отдаёт мультизначение как readonly-массив (`MultiValue<Option>`);
    // тип повторяет это, чтобы обработчик описывал фактический контракт компонента.
    const handleAdminIdsChange = (options: readonly SelectOption[] | null) => {
        setAdminIds((options ?? []).map((option) => option.value))
        setPage(1)
    }

    const handleActionTypesChange = (options: readonly SelectOption[] | null) => {
        setActionTypes((options ?? []).map((option) => option.value))
        setPage(1)
    }

    const handlePeriodChange = (nextPeriod: PeriodValue) => {
        setPeriod(nextPeriod)
        setPage(1)
    }

    // Экспорт повторяет ровно текущие фильтры списка и не трогает ни таблицу,
    // ни страницу, ни раскрытую строку.
    const handleExport = useCallback((format: JournalExportFormat) => {
        return exportAdminAuditLog(
            {
                adminIds,
                actionTypes,
                periodFrom: period.from,
                periodTo: period.to,
                page,
                pageSize: PAGE_SIZE,
            },
            format
        )
    }, [actionTypes, adminIds, page, period.from, period.to])

    const exportFileName = useCallback(
        (format: JournalExportFormat) => `admin-actions.${format}`,
        []
    )

    const handleEventClick = (eventId: string) => {
        setExpandedEventId((current) => current === eventId ? null : eventId)
    }

    const totalCount = auditData?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

    return (
        <div className="_page_container">
            <PageHeader title="Действия администраторов" />
            <JournalExportButtons onExport={handleExport} fallbackFileName={exportFileName} />
            <div className="flexbox">
                <div className="box">
                    <h2 className="mb-5 text-lg font-bold screen:text-2xl">Фильтры</h2>
                    <PeriodPicker
                        value={period}
                        onChange={handlePeriodChange}
                        withInterval={false}
                    />
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Select
                            isMulti
                            isLoading={referencesLoading}
                            options={adminOptions}
                            value={selectedAdminOptions}
                            placeholder="Все администраторы"
                            onChange={handleAdminIdsChange}
                        />
                        <Select
                            isMulti
                            isLoading={referencesLoading}
                            options={actionTypeOptions}
                            value={selectedActionTypeOptions}
                            placeholder="Все типы действий"
                            onChange={handleActionTypesChange}
                        />
                    </div>
                    {referencesError && (
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#FFF4F4] p-3">
                            <span className="text-color-danger">
                                Не удалось загрузить справочники: {referencesError}
                            </span>
                            <button
                                type="button"
                                onClick={fetchReferences}
                                className="form_handle_button !h-10 !w-auto px-5"
                            >
                                Повторить
                            </button>
                        </div>
                    )}
                </div>

                {logLoading && (
                    <div className="info_panel flex min-h-24 items-center justify-center">
                        <Loader />
                    </div>
                )}

                {!logLoading && logError && (
                    <div className="info_panel flex flex-wrap items-center justify-between gap-4">
                        <span className="text-color-danger">{logError}</span>
                        <button
                            type="button"
                            onClick={fetchAuditLog}
                            className="form_handle_button !w-auto px-6"
                        >
                            Повторить
                        </button>
                    </div>
                )}

                {!logLoading && !logError && auditData?.items.length === 0 && (
                    <NoTableData label="Нет данных за выбранный период" />
                )}

                {!logLoading && !logError && Boolean(auditData?.items.length) && (
                    <div className="table_box">
                        <div className="overflow-auto">
                            <table className="react-table min-w-[1050px]">
                                <thead>
                                    <tr>
                                        <th className="!text-left">Дата / время</th>
                                        <th className="!text-left">Администратор</th>
                                        <th className="!text-left">Тип действия</th>
                                        <th className="!text-left">Цель</th>
                                        <th className="!text-left">Исход</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {auditData?.items.map((event) => {
                                        const isExpanded = expandedEventId === event.id
                                        const target = [event.target_type, event.target_id]
                                            .filter(Boolean)
                                            .join(" / ") || "—"

                                        return (
                                            <Fragment key={event.id}>
                                                <tr
                                                    onClick={() => handleEventClick(event.id)}
                                                    onKeyDown={(keyboardEvent) => {
                                                        if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                                                            keyboardEvent.preventDefault()
                                                            handleEventClick(event.id)
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    aria-expanded={isExpanded}
                                                    className="cursor-pointer transition hover:bg-[#F7F8FA]"
                                                >
                                                    <td>{formatDateTime(event.created_at)}</td>
                                                    <td className="!whitespace-normal">
                                                        {event.admin_login}
                                                        {deletedAdminIds.has(event.admin_id) && " (удалён)"}
                                                    </td>
                                                    <td className="!whitespace-normal">
                                                        {resolveActionTypeLabel(event.action_type)}
                                                    </td>
                                                    <td className="!whitespace-normal break-all">{target}</td>
                                                    <td className="!whitespace-normal">
                                                        <span className={event.result === "error" ? "text-color-danger" : undefined}>
                                                            {resolveResultLabel(event.result)}
                                                        </span>
                                                        {event.result === "error" && event.error_code && (
                                                            <span className="ml-2 text-color-danger">
                                                                {event.error_code}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td
                                                            colSpan={5}
                                                            className="!whitespace-normal !bg-[#F7F8FA] !px-5"
                                                        >
                                                            <EventDetails event={event} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={auditData?.page ?? page}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            setPage={setPage}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
